import React from 'react';
import { useTranslation } from 'react-i18next';
import { ReviewPathology } from '@myriadgenetics/mgh-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faUsers } from '@fortawesome/free-solid-svg-icons';
import get from 'lodash.get';
import { useHistory, useLocation } from '../../../helpers/routing';
import { isSimonMedUrl } from '../../../helpers/url';
import { MappedRelative } from '../cancer-history-review/helper';
import { removeEmptyArraysFromCancerHistory } from './helper';
import CancerHistoryCard from '../cancer-history-card';

import './index.scss';
import { Text1 } from '../../../elements/typography';
import Button from '../../../elements/button';
import { CancerHistoryLocationState } from '../../survey-node/cancer-history-node';
import { getNextPathologyEntry } from '../../survey-node/cancer-history-node/helper';
import { GTMDataProps } from '../../../helpers/googleTagManager';

interface Props {
  allRelativesPathologies: MappedRelative[];
  baseKey: string;
  baseUrl?: string;
  onResponseUpdated: (answerKey: string, val: any, gtmData?: Partial<GTMDataProps>, replace?: boolean) => void;
  responses: any;
  setConfirmDelete: (confirmDelete: { translatedMessage: string; onConfirmation: () => void }) => void;
}

const REQUIRED_PATHOLOGY_KEYS = ['type'];

function CancerHistoryCards({ allRelativesPathologies, baseKey, baseUrl, onResponseUpdated, responses, setConfirmDelete }: Props) {
  const { t } = useTranslation();
  const history = useHistory();
  const location: CancerHistoryLocationState = useLocation() as CancerHistoryLocationState;
  const hasSelf = allRelativesPathologies.some((rp) => rp.relative === 'relatives.self' && rp.pathologies.length > 0);
  const hasFamily = allRelativesPathologies.some((rp) => rp.relative !== 'relatives.self' && rp.pathologies.length > 0);

  const clearPathologyStateFromCancer = (pathologyEntry: string, pathology: ReviewPathology) => {
    const minimumPathologyObj = {};
    REQUIRED_PATHOLOGY_KEYS.forEach((pathologyKey) => {
      if ((pathology as any)[pathologyKey] !== undefined) {
        (minimumPathologyObj as any)[pathologyKey] = (pathology as any)[pathologyKey];
      }
    });

    return onResponseUpdated(pathologyEntry, minimumPathologyObj, undefined, false);
  };

  return (
    <div className="cancer-history-cards">
      <div className={`cancer-history-cards__wrapper ${!hasSelf ? 'cancer-history-cards__no-history' : ''}`}>
        <div className="cancer-history-cards__title-wrapper">
          <FontAwesomeIcon icon={faUser} />
          <Text1 className="heading-medium--semi-bold">{t('self.history')}</Text1>
        </div>

        {/* Self card */}
        <div className="cancer-history-cards__card">
          {(() => {
            const rp = allRelativesPathologies.find((rp) => rp.relative === 'relatives.self');
            if (rp && rp.pathologies.length > 0) {
              const relEntryNum = (rp.num || 1) - 1;
              const relativeEntry = `${baseKey}.${rp.relativeKey}.${relEntryNum}`;
              return (
                <div className="cancer-history-review__list">
                  <CancerHistoryCard
                    relative={rp.relative}
                    num={rp.num || undefined}
                    pathologies={rp.pathologies as ReviewPathology[]}
                    onAddClicked={() => {
                      history.push(`${baseUrl}/add`, {
                        ...location.state,
                        chnState: {
                          baseUrl,
                          curStep: 'choose-pathology',
                          relativeEntry,
                          pathologyEntry: getNextPathologyEntry(responses, relativeEntry),
                          pathology: null,
                          fromReview: true,
                        },
                      });
                    }}
                    onEditClicked={(pathology, pI) => {
                      const pathologyBase = `${relativeEntry}.pathology`;
                      const pathologyEntry = `${pathologyBase}.${pI}`;
                      //  TODO: implement better state management
                      // This is a hacky way to remove the previously entered cancer data
                      const nextResponses = clearPathologyStateFromCancer(pathologyEntry, pathology as ReviewPathology);

                      history.push(`${baseUrl}/edit`, {
                        ...location.state,
                        locResponses: nextResponses,
                        chnState: {
                          baseUrl,
                          relativeEntry,
                          pathologyEntry,
                          pathology: pathology.type,
                          curStep: 'pathology',
                          fromReview: true,
                        },
                      });
                    }}
                    onDeleteClicked={(pathology, pI) => {
                      const pathologyBase = `${relativeEntry}.pathology`;
                      const pathologies = get(responses, pathologyBase, []);
                      const pathologyEntry = `${pathologyBase}.${pI}`;

                      setConfirmDelete({
                        translatedMessage: t('pathology.confirmDelete', pathologies[pI]),
                        onConfirmation: () => {
                          // add jump step to do undelete on back
                          const updatedPathologies = pathologies.filter((p: ReviewPathology, pfI: number) => pI !== pfI);
                          clearPathologyStateFromCancer(pathologyEntry, pathology as ReviewPathology);

                          onResponseUpdated(pathologyBase, updatedPathologies);
                          if (updatedPathologies.length === 0) {
                            removeEmptyArraysFromCancerHistory(rp.relative, responses, baseKey, onResponseUpdated);
                          }

                          history.replace(`${baseUrl}/del`, {
                            ...location.state,
                          });
                        },
                      });
                    }}
                  />
                </div>
              );
            } else {
              return (
                <CancerHistoryCard
                  pathologies={[]}
                  relative="relatives.self"
                  onEditClicked={(pathology, pI) => {
                    const relativeEntry = 'cancerHistory.relatives.self.0';
                    const pathologyBase = `${relativeEntry}.pathology`;
                    const pathologyEntry = `${pathologyBase}.${pI}`;
                    //  TODO: implement better state management
                    // This is a hacky way to remove the previously entered cancer data
                    const nextResponses = clearPathologyStateFromCancer(pathologyEntry, pathology as ReviewPathology);

                    history.push(`${baseUrl}/edit`, {
                      ...location.state,
                      locResponses: nextResponses,
                      chnState: {
                        baseUrl,
                        relativeEntry,
                        pathologyEntry,
                        pathology: pathology.type,
                        curStep: 'pathology',
                        fromReview: true,
                      },
                    });
                  }}
                  onAddClicked={() => {
                    const relativeEntry = 'cancerHistory.relatives.self.0';
                    history.push(`${baseUrl}/add`, {
                      ...location.state,
                      chnState: {
                        baseUrl,
                        curStep: 'choose-pathology',
                        relativeEntry,
                        pathologyEntry: getNextPathologyEntry(responses, relativeEntry),
                        pathology: null,
                        fromReview: true,
                      },
                    });
                  }}
                />
              );
            }
          })()}
        </div>
      </div>

      {/* Relative card */}
      <div className={`cancer-history-cards__wrapper ${!hasFamily ? 'cancer-history-cards__no-history' : ''}`}>
        <div className="cancer-history-cards__title-wrapper">
          <FontAwesomeIcon icon={faUsers} />
          <Text1 className="heading-medium--semi-bold">{t('relatives.history')}</Text1>
        </div>
        <div className="cancer-history-cards__card-column">
          {!hasFamily && (
            <CancerHistoryCard
              relative={null}
              pathologies={[]}
              onAddClicked={() => {
                history.push(`${baseUrl}/add`, {
                  ...location.state,
                  chnState: {
                    baseUrl,
                    curStep: 'choose-relative',
                    fromReview: true,
                  },
                });
              }}
            />
          )}
          {allRelativesPathologies.map((rp, i) => {
            const relEntryNum = (rp.num || 1) - 1;
            const relativeEntry = `${baseKey}.${rp.relativeKey}.${relEntryNum}`;
            if (rp.relative !== 'relatives.self' && rp.pathologies && rp.pathologies.length > 0) {
              return (
                <div className="cancer-history-review__list" key={i}>
                  <CancerHistoryCard
                    relative={rp.relative}
                    num={rp.num || undefined}
                    pathologies={rp.pathologies as ReviewPathology[]}
                    onAddClicked={() => {
                      history.push(`${baseUrl}/add`, {
                        ...location.state,
                        chnState: {
                          baseUrl,
                          curStep: 'choose-pathology',
                          relativeEntry,
                          pathologyEntry: getNextPathologyEntry(responses, relativeEntry),
                          pathology: null,
                          fromReview: true,
                        },
                      });
                    }}
                    onEditClicked={(pathology, pI) => {
                      const pathologyBase = `${relativeEntry}.pathology`;
                      const pathologyEntry = `${pathologyBase}.${pI}`;
                      //  TODO: implement better state management
                      // This is a hacky way to remove the previously entered cancer data
                      const nextResponses = clearPathologyStateFromCancer(pathologyEntry, pathology as ReviewPathology);

                      history.push(`${baseUrl}/edit`, {
                        ...location.state,
                        locResponses: nextResponses,
                        chnState: {
                          baseUrl,
                          relativeEntry,
                          pathologyEntry,
                          pathology: pathology.type,
                          curStep: 'pathology',
                          fromReview: true,
                        },
                      });
                    }}
                    onDeleteClicked={(pathology, pI) => {
                      const pathologyBase = `${relativeEntry}.pathology`;
                      const pathologies = get(responses, pathologyBase, []);
                      const pathologyEntry = `${pathologyBase}.${pI}`;

                      setConfirmDelete({
                        translatedMessage: t('pathology.confirmDelete', pathologies[pI]),
                        onConfirmation: () => {
                          // add jump step to do undelete on back
                          const updatedPathologies = pathologies.filter((p: ReviewPathology, pfI: number) => pI !== pfI);
                          clearPathologyStateFromCancer(pathologyEntry, pathology as ReviewPathology);

                          onResponseUpdated(pathologyBase, updatedPathologies);
                          if (updatedPathologies.length === 0) {
                            removeEmptyArraysFromCancerHistory(rp.relative, responses, baseKey, onResponseUpdated);
                          }

                          history.replace(`${baseUrl}/del`, {
                            ...location.state,
                          });
                        },
                      });
                    }}
                  />
                </div>
              );
            }
            return null;
          })}
        </div>
        <div className="cancer-history-review__add-relative">
          <Button
            className={`cancer-history-review__add-relative-button text-medium--medium ${
              isSimonMedUrl() ? 'cancer-history-review__add-relative-button--smi' : ''
            }`}
            secondary
            onClick={() => {
              history.push(`${baseUrl}/add`, {
                ...location.state,
                chnState: {
                  baseUrl,
                  curStep: 'choose-relative',
                  fromReview: true,
                },
              });
            }}
          >
            {t('relatives.add')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default CancerHistoryCards;
